/**
 * Projects Page
 *
 * Lists all development projects (subdivisions, townhomes, apartments, etc.)
 * Provides navigation to individual project dashboards and creation wizard.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/hooks/useProject";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Building2,
  Home,
  Layers,
  MapPin,
  Loader2,
  ChevronRight,
  FolderKanban,
  AlertCircle,
} from "lucide-react";
import type {
  DevelopmentProject,
  DevelopmentProjectType,
  DevelopmentProjectStatus,
} from "@/types/project";

/**
 * Get icon for development type
 */
function getProjectTypeIcon(type: DevelopmentProjectType) {
  switch (type) {
    case "townhome":
      return <Home className="h-5 w-5" />;
    case "apartment":
      return <Building2 className="h-5 w-5" />;
    case "subdivision":
      return <Layers className="h-5 w-5" />;
    case "mixed_use":
      return <FolderKanban className="h-5 w-5" />;
    case "commercial_complex":
      return <Building2 className="h-5 w-5" />;
    default:
      return <Home className="h-5 w-5" />;
  }
}

/**
 * Get display name for development type
 */
function getProjectTypeName(type: DevelopmentProjectType): string {
  const names: Record<DevelopmentProjectType, string> = {
    townhome: "Townhome Community",
    apartment: "Apartment Complex",
    subdivision: "Subdivision",
    mixed_use: "Mixed-Use",
    commercial_complex: "Commercial Complex",
    single_property: "Single Property",
  };
  return names[type] || type;
}

/**
 * Get badge variant for project status
 */
function getStatusBadge(status: DevelopmentProjectStatus) {
  const config: Record<
    DevelopmentProjectStatus,
    { variant: "default" | "secondary" | "outline" | "destructive"; label: string }
  > = {
    planning: { variant: "secondary", label: "Planning" },
    permitting: { variant: "default", label: "Permitting" },
    construction: { variant: "outline", label: "Construction" },
    closeout: { variant: "outline", label: "Closeout" },
    complete: { variant: "secondary", label: "Complete" },
  };
  const { variant, label } = config[status] || { variant: "secondary", label: status };
  return <Badge variant={variant}>{label}</Badge>;
}

/**
 * Project Card Component
 */
function ProjectCard({
  project,
  onClick,
}: {
  project: DevelopmentProject;
  onClick: () => void;
}) {
  const { unitStats, sharedPermitStats } = project;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              {getProjectTypeIcon(project.developmentType)}
            </div>
            <div>
              <CardTitle className="text-lg">{project.name}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {getProjectTypeName(project.developmentType)}
              </p>
            </div>
          </div>
          {getStatusBadge(project.status)}
        </div>
      </CardHeader>
      <CardContent>
        {/* Address */}
        {project.masterAddress && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <MapPin className="h-4 w-4" />
            <span>
              {project.masterAddress.city}, {project.masterAddress.state}
            </span>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 pt-3 border-t">
          <div className="text-center">
            <p className="text-2xl font-semibold">{project.totalUnits}</p>
            <p className="text-xs text-muted-foreground">Units</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-green-600">
              {unitStats?.ready || 0}
            </p>
            <p className="text-xs text-muted-foreground">Ready</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-amber-600">
              {unitStats?.blocked || 0}
            </p>
            <p className="text-xs text-muted-foreground">Blocked</p>
          </div>
        </div>

        {/* Shared Permits Summary */}
        {sharedPermitStats && sharedPermitStats.total > 0 && (
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Shared Permits</span>
              <span>
                {sharedPermitStats.issued}/{sharedPermitStats.total} issued
              </span>
            </div>
          </div>
        )}

        {/* View Button */}
        <div className="mt-4 flex justify-end">
          <Button variant="ghost" size="sm" className="gap-1">
            View Project
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Empty State Component
 */
function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="p-4 bg-muted rounded-full mb-4">
        <Building2 className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No Projects Yet</h3>
      <p className="text-muted-foreground max-w-md mb-6">
        Create your first development project to manage multiple units, track
        permits across your entire portfolio, and streamline compliance.
      </p>
      <Button onClick={onCreateClick} className="gap-2">
        <Plus className="h-4 w-4" />
        Create Project
      </Button>
    </div>
  );
}

/**
 * Projects Page Component
 */
const Projects = () => {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const { projects, isLoading, error } = useProjects();

  // Auth redirect
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth");
      return;
    }
  }, [navigate, user, authLoading]);

  const handleCreateProject = () => {
    navigate("/projects/new");
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertCircle className="h-8 w-8 text-destructive mb-4" />
        <h3 className="text-lg font-semibold mb-2">Failed to Load Projects</h3>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <div className="container max-w-7xl py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Development Projects</h1>
          <p className="text-muted-foreground">
            Manage subdivisions, townhomes, apartments, and multi-unit developments
          </p>
        </div>
        {projects.length > 0 && (
          <Button onClick={handleCreateProject} className="gap-2">
            <Plus className="h-4 w-4" />
            New Project
          </Button>
        )}
      </div>

      {/* Projects Grid or Empty State */}
      {projects.length === 0 ? (
        <EmptyState onCreateClick={handleCreateProject} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => handleProjectClick(project.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;
